<template>
  <div class="management">
    <v-snackbar
      v-model="snackbar"
      :timeout="timeout"
    >
      {{ toastMessage }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="blue"
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <v-data-table
      :loading="externalLoading"
      :headers="headers"
      :items="items"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :server-items-length="total"
      :items-per-page="limit"
      :footer-props="footerProps"
      :show-expand="showExpand"
      :expanded="expanded"
      :single-expand="singleExpand"
      :hide-default-header="$vuetify.breakpoint.mobile"
      class="elevation-1"
      @pagination="handlePagination"
      @update:options="handleOptions"
      @update:expanded="handleExpanded"
    >
      <template v-slot:expanded-item="{ headers, item }">
        <slot
          name="expanded-item"
          :headers="headers"
          :item="item"
        ></slot>
      </template>
      <template
        v-for="h in headers"
        v-slot:[`header.${h.value}`]="{ header }"
      >
        {{ getTranslation(header.text) }}
      </template>
      <template
        v-for="col in customColumns"
        v-slot:[getSlotName(col.name)]="{ value, item }"
      >
        <section
          v-if="col.callback"
          :key="col.name"
        >
          {{ col.callback(value, item) }}
        </section>
        <slot
          :name="getSlotName(col.name)"
          :value="value"
          :item="item"
        ></slot>
      </template>
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>{{ getTranslation(mFormConfig.title) }}</v-toolbar-title>
          <v-divider
            class="mx-4"
            inset
            vertical
          ></v-divider>
          <v-dialog
            v-model="dialog"
            persistent
            :max-width="editWith"
            @click:outside="clickOutside"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                class="ml-5"
              >
                {{ refreshMessage }}
              </div>
              <v-btn
                v-if="!disableCreate"
                color="primary"
                dark
                class="mb-2"
                v-bind="attrs"
                v-on="on"
                @click="handleDefaultItem"
              >
                {{ getTranslation(mFormConfig.create.btnText) }}
              </v-btn>
              <v-btn
                v-if="enablePrint"
                color="primary"
                dark
                class="mb-2 mr-2"
                @click="handlePrintALL"
              >
                {{ `Print QRCode` }}
              </v-btn>
              <v-btn
                :loading="loading"
                color="primary"
                dark
                class="mb-2 mr-2"
                @click="fetchData"
              >
                {{ getTranslation(mFormConfig.refresh) }}
              </v-btn>
              <slot name="custom-button">
              </slot>
            </template>
            <v-card>
              <v-card-title class="no-bmp">
                <span class="headline">{{ getTranslation(formTitle) }}</span>
                <v-spacer></v-spacer>
                <v-btn
                  class="mr-1"
                  dark
                  color="blue darken-1"
                  depressed
                  :loading="loading"
                  @click="save"
                >
                  {{ getTranslation(btnSave) }}
                </v-btn>
                <v-btn
                  color="secondary"
                  depressed
                  dark
                  @click="close"
                >
                  {{ getTranslation(btnCancel) }}
                </v-btn>
              </v-card-title>
              <v-form
                ref="form"
                v-model="valid"
                class="form"
                lazy-validation
              >
                <v-row no-gutters>
                  <v-col cols="leftCols">
                    <v-card-text>
                      <v-container>
                        <input-container
                          ref="inputContainer"
                          :configs="inputConfigs"
                        ></input-container>
                      </v-container>
                    </v-card-text>
                  </v-col>
                  <v-col
                    v-if="rightCols"
                    cols="rightCols"
                  >
                    <slot name="extraInput"></slot>
                  </v-col>
                </v-row>
              </v-form>
            </v-card>
          </v-dialog>
          <v-dialog
            v-model="dialogDelete"
            :max-width="editWith"
            @click:outside="clickOutside"
          >
            <v-card>
              <v-card-title class="no-bmp">
                <span class="headline">{{ getTranslation(mFormConfig.delete.title || 'delete') }}</span>
                <v-spacer></v-spacer>
                <v-btn
                  v-if="!disableDelete"
                  class="mr-1"

                  color="error darken-1"
                  depressed
                  :loading="loading"
                  @click="deleteItemConfirm"
                >
                  {{ getTranslation(mFormConfig.delete.confirm || 'confirm') }}
                </v-btn>
                <v-btn
                  color="secondary"
                  depressed
                  dark
                  @click="closeDelete"
                >
                  {{ getTranslation(mFormConfig.delete.cancel || 'cancel') }}
                </v-btn>
              </v-card-title>
              <v-form
                ref="delForm"
                class="form"
                lazy-validation
              >
                <v-row no-gutters>
                  <v-col cols="leftCols">
                    <v-card-text>
                      <v-container>
                        <input-container
                          ref="delInputContainer"
                          :configs="delInputConfigs"
                        ></input-container>
                      </v-container>
                    </v-card-text>
                  </v-col>
                  <v-col
                    v-if="rightCols"
                    cols="rightCols"
                  >
                    <slot name="extraInput"></slot>
                  </v-col>
                </v-row>
              </v-form>
            </v-card>
          </v-dialog>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-text-field
          v-if="enableSearch"
          v-model="searchKeyWord"
          :label="searchLabel"
          class="mx-4"
        ></v-text-field>
        <slot name="extra-top"></slot>
      </template>
      <template v-slot:item.actions="{ item }">
        <span
          v-if="$vuetify.breakpoint.mobile"
          class="mobile-action"
        >
          <slot
            name="item.prependActions"
            :item="item"
          ></slot>
          <v-icon
            v-if="!disableUpdate"
            class="action-icon mr-2"
            @click="editItem(item)"
          >
            {{ mFormConfig.editIcon }}
          </v-icon>
          <v-icon
            v-if="enablePrint"
            class="action-icon"
            @click="printCallBackAction(item)"
          >
            {{ mFormConfig.printerIcon }}
          </v-icon>
          <v-icon
            v-if="!disableDelete"
            class="action-icon"
            @click="deleteItem(item)"
          >
            {{ mFormConfig.deleteIcon }}
          </v-icon>
        </span>
        <span v-else>
          <slot
            name="item.prependActions"
            :item="item"
          ></slot>
          <v-icon
            v-if="!disableUpdate"
            class="action-icon mr-2"
            @click="editItem(item)"
          >
            {{ mFormConfig.editIcon }}
          </v-icon>
          <v-icon
            v-if="enablePrint"
            class="action-icon"
            @click="printCallBackAction(item)"
          >
            {{ mFormConfig.printerIcon }}
          </v-icon>
          <v-icon
            v-if="!disableDelete"
            class="action-icon"
            @click="deleteItem(item)"
          >
            {{ mFormConfig.deleteIcon }}
          </v-icon>

        </span>
      </template>
    </v-data-table>
  </div>
</template>

<script>

// import { fetchList } from '@/api/post'
import InputContainer from './InputContainer'

// import UniversalInput from './UniversalInput'
export default {
  name: 'CRUDTable',
  components: {
    InputContainer,

    // UniversalInput
  },
  props: {
    expanded: {
      type: Array,
      default() {
        return []
      },
    },
    singleExpand: {
      type: Boolean,
      default: false,
    },
    showExpand: {
      type: Boolean,
      default: false,
    },
    enableSearch: {
      type: Boolean,
      default: true,
    },

    // extraInputValidation: {
    //   type: Function,
    //   default() {
    //     return () => true
    //   },
    // },
    extraInputValidation: {
      type: Boolean,
      default: true,
    },
    extraInputCallBack: {
      type: Function,
      default: null,
    },
    extraInputCols: {
      type: Number,
      default: 0,
    },
    deleteWith: {
      type: String,
      default: '500px',
    },
    editWith: {
      type: String,
      default: '500px',
    },
    remoteCreateItem: {
      type: Function,
    },
    remoteUpdateItem: {
      type: Function,
    },
    remoteDeleteItem: {
      type: Function,
    },
    remoteGetItem: {
      type: Function,
    },
    fetchList: {
      type: Function,
    },
    formConfig: {
      type: Object,
    },
    inputConfigs: {
      type: Array,
    },
    disableUpdate: {
      type: Boolean,
    },
    disableDelete: {
      type: Boolean,
    },
    disableCreate: {
      type: Boolean,
    },
    enablePrint: {
      type: Boolean,
    },

    printCallback: {
      default: null,
      type: Function,
    },
    handlePrintThisCenterQRCode: {
      default: null,
      type: Function,
    },
    externalLoading: {
      default: false,
      type: Boolean,
    },
    createParamsCallback: {
      default: null,
      type: Function,
    },
    autoRefresh: {
      default: true,
      type: Boolean,
    },
    refreshIntervalSeconds: {
      default: 60,
      type: Number,
    },
  },
  data() {
    return {
      refreshMessage: '',
      interval: null,
      snackbar: false,
      timeout: 3000,
      toastMessage: '',
      mFormConfig: {
        limit: 20,
        editIcon: 'mdi-pencil',
        deleteIcon: 'mdi-delete',
        printerIcon: 'mdi-printer',
        empty: 'Empty',
        refresh: 'Refresh',
        create: {
          btnText: 'New',
          title: 'New',
          btnSave: 'Save',
          btnCancel: 'Cancel',
        },
        update: {
          title: 'Edit',
          btnSave: 'Update',
          btnCancel: 'Cancel',
        },
        delete: {
          title: 'Delete',
          cancel: 'Cancel',
          confirm: 'Confirm',
        },
      },
      internalSortBy: 'id',
      internalSortDesc: false,
      searchKeyWord: '',
      deleteObjClone: null,
      onDeleteShowContent: true,
      valid: false,
      loading: false,
      extraInputItem: null,
      defaultCustomColumns: [],
      items: [],
      total: -1,
      page: 1,
      defaultLimit: 20,
      limit: 20,
      dialog: false,
      dialogDelete: false,
      editedIndex: -1,

      // defaultItem: {
      // },
      defaultFooterProps: {
        itemsPerPageOptions: [20, 30, 50, -1], // -> Add this example
        showFirstLastPage: true,
      },
    }
  },
  computed: {
    searchLabel() {
      return this.mFormConfig.searchLabel ? this.mFormConfig.searchLabel : 'Search (Minimal input 2 Characters)'
    },
    delInputConfigs() {
      const cloneConfigs = []
      this.inputConfigs.forEach(config => {
        const delConfig = { ...config }
        delConfig.readonly = true
        cloneConfigs.push(delConfig)
      })

      return cloneConfigs
    },
    defaultItem() {
      const item = {}
      this.inputConfigs.forEach(config => {
        if (Object.prototype.hasOwnProperty.call(config, 'defaultValue')) {
          item[config.propName] = config.defaultValue
        }
      })
      console.log('get defaultItem', item)

      return item
    },
    rightCols() {
      console.log('this.$slots', this.$slots)
      if (this.$slots) {
        if (this.hasExtraInput) {
          return this.extraInputCols || 6
        }
      }

      return 0
    },
    hasExtraInput() {
      return Object.prototype.hasOwnProperty.call(this.$slots, 'extraInput')
    },
    leftCols() {
      return 12 - this.rightCols
    },
    formTitle() {
      return this.editedIndex === -1 ? this.mFormConfig.create.title : this.mFormConfig.update.title
    },
    btnSave() {
      return this.editedIndex === -1 ? this.mFormConfig.create.btnSave : this.mFormConfig.update.btnSave
    },
    btnCancel() {
      return this.editedIndex === -1 ? this.mFormConfig.create.btnCancel : this.mFormConfig.update.btnCancel
    },
    footerProps() {
      if (this.mFormConfig.footerProps) {
        return this.mFormConfig.footerProps
      }

      return this.defaultFooterProps
    },
    customColumns() {
      return this.mFormConfig.customColumns ? this.mFormConfig.customColumns : this.defaultCustomColumns
    },
    headers() {
      const headers = this.mFormConfig.headers.slice()
      console.log('headers', headers)

      // headers.forEach(header => {
      //   header.text = this.$t(header.text)
      // })
      return headers
    },
    sortBy() {
      return this.mFormConfig.sortBy ? this.mFormConfig.sortBy : this.internalSortBy
    },
    sortDesc() {
      return typeof this.mFormConfig.sortDesc !== 'undefined' ? this.mFormConfig.sortDesc : this.internalSortDesc
    },
  },

  watch: {
    dialog(val) {
      console.log('dialog changed', val)

      return val || this.close()
    },
    dialogDelete(val) {
      return val || this.closeDelete()
    },
    searchKeyWord(val) {
      console.log('trigger searchKeyWord', val)

      // 0 would reset character 2 start to search
      if (val.length !== 1) {
        this.fetchData()
      }
    },
  },

  created() {
    // console.log('this.mFormConfig', this.mFormConfig)
    this.mFormConfig = Object.assign(this.mFormConfig, this.formConfig)

    // console.log('this.mFormConfig', this.mFormConfig)

    this.orderBy = this.sortBy
    this.orderDesc = this.sortDesc

    // console.log('this.mFormConfig.limit', this.mFormConfig.limit)
    // console.log('this.defaultLimit', this.defaultLimit)
    this.limit = this.mFormConfig.limit ? this.mFormConfig.limit : this.defaultLimit

    // console.log('this.limit', this.limit)
  },
  mounted() {
    this.interval = setInterval(() => {
      this.$nextTick(() => {
        console.log('Table Start Refresh.')
        this.fetchData()
      })
    }, this.refreshIntervalSeconds * 1000)
  },
  destroyed() {
    console.log('Table Clear Refresh Interval.')
    clearInterval(this.interval)
  },
  methods: {
    showToastMessage(msg) {
      this.toastMessage = msg
    },
    handleExpanded(expandedRows) {
      this.$emit('update:expanded', expandedRows)
    },
    handleOptions(options) {
      console.log('handleOptions options', options)
      this.orderBy = this.sortBy
      this.orderDesc = this.sortDesc
      if (options.sortBy && options.sortBy.length > 0) {
        this.orderBy = options.sortBy[0]
        this.orderDesc = options.sortDesc[0]
      }
      this.fetchData()
    },
    handlePagination(pageInfo) {
      console.log('pageInfo', pageInfo)
      if (pageInfo.itemsPerPage == -1) {
        this.page = 0
        this.limit = 0
      } else {
        this.page = pageInfo.page
        this.limit = pageInfo.itemsPerPage
      }
      console.log('handlePagination')
      this.fetchData()
    },
    clickOutside(event) {
      const r = confirm(this.$t('message.component.crud_table.leave_dialog'))
      if (r === true) {
        this.dialog = false
        this.dialogDelete = false
      }
    },
    handleDefaultItem() {
      console.log('handleDefaultItem', this.defaultItem)
      this.setEditedItem(this.defaultItem)
    },
    getTranslation(text) {
      if (text.includes('message.')) {
        return this.$t(text)
      }

      return text
    },
    getSlotName(slotName) {
      // console.log(`item.${slotName}`)

      return `item.${slotName}`
    },
    setEditedItem(item) {
      const cloneItem = { ...item }
      if (this.$refs.inputContainer) {
        this.$set(this.$refs.inputContainer, 'editedItem', cloneItem)
      }
      this.extraInputItem = cloneItem
    },
    setDelEditedItem(item) {
      const cloneItem = { ...item }
      if (this.$refs.delInputContainer) {
        this.$set(this.$refs.delInputContainer, 'editedItem', cloneItem)
      }
    },
    getEditedItem() {
      // console.log('getEditedItem')
      if (this.$refs.inputContainer) {
        console.log('this.$refs.inputContainer.editedItem')

        // console.log('this.$refs.inputContainer.editedItem', this.$refs.inputContainer.editedItem)
        if (this.extraInputCallBack) {
          console.log('insideextraInputCallBack')

          return this.extraInputCallBack(this.$refs.inputContainer.editedItem)
        }

        return this.$refs.inputContainer.editedItem
      }

      return null
    },
    createParams() {
      let params = {
        page: this.page,
        limit: this.limit,
        searchKeyWord: this.searchKeyWord,
        orderBy: this.orderBy,
        orderDesc: this.orderDesc,
      }
      if (this.createParamsCallback) {
        params = this.createParamsCallback(params)
      }

      return params
    },
    fetchData() {
      this.loading = true
      const params = this.createParams()

      return this.fetchList(params).then(data => {
        console.log('fetchList data', data)
        this.items = data.data.items
        this.total = data.data.total
        this.loading = false
        this.$emit('getList', this.items)

        this.refreshMessage = 'Table Refreshed.'
        setTimeout(() => {
          this.refreshMessage = ''
        }, 1500)
      })
    },

    editItem(item) {
      this.editedIndex = this.items.indexOf(item)
      const action = this.editedIndex > -1 ? 'edit' : 'create'

      // get item from remote if remoteGetItem Exist
      if (this.remoteGetItem) {
        this.remoteGetItem(item.id).then(response => {
          this.$emit('openEditDialog', action)
          console.log('remoteGetItem', response)
          this.dialog = true
          this.$nextTick(() => {
            this.setEditedItem(response.data.record)
            if (this.hasExtraInput) {
              this.$emit('setupExtraInput', response.data.record)
            }
          })
        })
      } else {
        this.dialog = true
        this.$nextTick(() => {
          // console.log('this.$refs.inputContainer', this.$refs.inputContainer)
          this.setEditedItem(item)
        })
      }
    },
    deleteItem(item) {
      this.editedIndex = this.items.indexOf(item)
      this.dialogDelete = true
      this.$emit('openDeleteDialog', item)
      this.$nextTick(() => {
        this.setDelEditedItem(item)
      })
    },

    deleteItemConfirm() {
      const item = this.items[this.editedIndex]

      // remote delete item
      this.remoteDeleteItem(item)
        .then(response => {
          console.log('delete response', response)
          this.$emit('saved')
          this.fetchData()
          this.closeDelete()
        })
        .catch(error => {
          console.log('delete error', error)
          this.$emit('saved')
          this.fetchData()
          this.closeDelete()
        })
    },

    printCallBackAction(item) {
      this.$emit('QRCodePrint', item)
    },

    handlePrintALL() {
      return this.handlePrintThisCenterQRCode(this.items)
    },

    close() {
      this.dialog = false
      this.$emit('openDeleteDialog', false)
      this.$nextTick(() => {
        // this.editedItem = Object.assign({}, this.defaultItem)
        this.setEditedItem(this.defaultItem)
        this.editedIndex = -1
        this.$refs.form.resetValidation()
        this.$emit('reset')
      })
    },

    closeDelete() {
      this.dialogDelete = false
      this.$emit('openDeleteDialog', false)
      this.$nextTick(() => {
        this.setDelEditedItem(this.defaultItem)
        this.editedIndex = -1
      })
    },

    async save() {
      if (this.$refs.form.validate()) {
        this.loading = true

        // update action
        let actionPromise = null
        if (this.editedIndex > -1) {
          // Object.assign(this.items[this.editedIndex], this.editedItem)
          console.log('this.editedIndex > -1')
          const getEditItem = await this.getEditedItem()
          console.log('getEditItem', getEditItem)

          // let avatarFile has been checked
          // await new Promise(resolve => setTimeout(resolve, 3000));
          if (this.extraInputValidation === false) {
            alert(this.$t('message.component.crud_table.required'))
            this.loading = false

            return
          }

          actionPromise = this.remoteUpdateItem(getEditItem)
        } else {
          console.log('!this.editedIndex > -1')
          const getCreateItem = await this.getEditedItem()

          // let avatarFile has been checked
          // await new Promise(resolve => setTimeout(resolve, 3000));
          if (this.extraInputValidation === false) {
            alert(this.$t('message.component.crud_table.required'))
            this.loading = false

            return
          }

          // this.items.push(this.editedItem)
          actionPromise = this.remoteCreateItem(getCreateItem)
        }
        if (!actionPromise) return
        actionPromise.then(response => {
          console.log('remoteUpdateItem', response)
          this.$emit('saved', response)

          // refresh the list after update
          this.fetchData().then(response => {
            this.close()
            this.showToastMessage('Record Saved.')
          })
        }).catch(error => {
          console.log('update error', error)
          this.$emit('saved')

          // refresh the list after update
          this.fetchData().then(response => {
            this.close()
            this.showToastMessage('Save Failed.')
          })
        })

        // create action
      } else {
        // this.showToast(this.$t('message.org.management.post.required'))
        alert(this.$t('message.component.crud_table.required'))
      }
    },
  },
}
</script>
<style scoped>
.no-bmp {
  margin-bottom: 0px !important;
  padding-bottom: 0px !important;
}

.v-data-table--mobile > .v-data-table__wrapper tbody {
  width:100vw;
}

@media only screen and (max-width: 776px){
  .v-data-table > .v-data-table__wrapper .v-data-table__mobile-row {
      height: initial;
      min-height: 24px;
  }
  .v-data-table__wrapper tbody {
    width: 100vw;
  }
}
</style>

<style>
.action-icon {
  vertical-align: baseline !important;
}
</style>
